/**
 * Asking the person for an SSH password, once, for one connection attempt.
 *
 * A forward answers a password or passphrase through an `SSH_ASKPASS` helper. A destination that
 * wants a password would otherwise fail as "refused the SSH credential", which is true and
 * useless: the way out is to type it.
 *
 * Two things raise a prompt here and they are answered the same way. Computer Setup asks from the
 * renderer, because the setup job it belongs to is already driven from here. A connection asks
 * from the main process, because that is where the forward and the askpass helper live, and the
 * request arrives over the desktop bridge.
 *
 * The secret is handed straight to the ssh process for that attempt and is never stored here. It
 * is held only for as long as the request is unanswered, and every request expires on its own so
 * a prompt nobody answers cannot keep an attempt open forever.
 */

export interface SshPasswordPromptRequest {
  readonly requestId: string;
  /** What the person is being asked to unlock, as they typed it: `user@host` or an SSH alias. */
  readonly destination: string;
  readonly prompt: string;
  readonly expiresAtMs: number;
}

/** Long enough to fetch a password from a manager, short enough not to strand an attempt. */
export const SSH_PASSWORD_PROMPT_TIMEOUT_MS = 120_000;

interface PendingPrompt {
  readonly request: SshPasswordPromptRequest;
  readonly resolve: (password: string | null) => void;
  readonly timer: ReturnType<typeof setTimeout>;
}

let nextRequestId = 0;
const pending = new Map<string, PendingPrompt>();
const listeners = new Set<(requests: ReadonlyArray<SshPasswordPromptRequest>) => void>();

function publish(): void {
  const requests = [...pending.values()].map((entry) => entry.request);
  for (const listener of listeners) listener(requests);
}

function settle(requestId: string, password: string | null): void {
  const entry = pending.get(requestId);
  if (entry === undefined) return;
  clearTimeout(entry.timer);
  pending.delete(requestId);
  entry.resolve(password);
  publish();
}

/** Subscribes to the open prompts. Returns the unsubscribe. */
export function subscribeSshPasswordPrompts(
  listener: (requests: ReadonlyArray<SshPasswordPromptRequest>) => void,
): () => void {
  listeners.add(listener);
  listener([...pending.values()].map((entry) => entry.request));
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Asks for one destination's password. Resolves with null when the person cancels or the request
 * expires, which the caller treats as "do not retry" rather than as an empty password.
 */
export function requestSshPassword(input: {
  readonly destination: string;
  readonly prompt?: string;
}): Promise<string | null> {
  // No dialog is mounted on a client that is not the desktop shell, so asking would hang.
  if (listeners.size === 0) return Promise.resolve(null);
  // A counter, not randomness: these ids never leave this module and only have to tell one open
  // prompt from another within a single session.
  nextRequestId += 1;
  const requestId = `ssh-password-${nextRequestId}`;
  return new Promise<string | null>((resolve) => {
    const timer = setTimeout(() => settle(requestId, null), SSH_PASSWORD_PROMPT_TIMEOUT_MS);
    pending.set(requestId, {
      resolve,
      timer,
      request: {
        requestId,
        destination: input.destination,
        prompt: input.prompt ?? `Password for ${input.destination}`,
        expiresAtMs: Date.now() + SSH_PASSWORD_PROMPT_TIMEOUT_MS,
      },
    });
    publish();
  });
}

/** Answers one prompt. Null is the person declining, which is a real answer. */
export function resolveSshPasswordPrompt(requestId: string, password: string | null): void {
  settle(requestId, password);
}

/**
 * Shows the prompts the main process raises while opening a forward.
 *
 * The main process owns the question — it holds the ssh process waiting on its askpass helper —
 * so this only displays it and sends the answer back. Its own timeout is authoritative; the one
 * kept here is a floor so a dialog cannot outlive a request main has already given up on.
 */
export function attachDesktopSshPasswordPrompts(): () => void {
  const ssh = window.desktopBridge?.ssh;
  if (ssh === undefined) return () => {};
  return ssh.onPasswordPrompt((request) => {
    const expiresAtMs = Date.parse(request.expiresAt);
    const remainingMs = Number.isFinite(expiresAtMs)
      ? Math.max(0, expiresAtMs - Date.now())
      : SSH_PASSWORD_PROMPT_TIMEOUT_MS;
    const timer = setTimeout(() => settle(request.requestId, null), remainingMs);
    pending.set(request.requestId, {
      timer,
      resolve: (password) => {
        void ssh.resolvePasswordPrompt(request.requestId, password);
      },
      request: {
        requestId: request.requestId,
        destination: request.destination,
        prompt: request.prompt,
        expiresAtMs: Number.isFinite(expiresAtMs) ? expiresAtMs : Date.now() + remainingMs,
      },
    });
    publish();
  });
}

/** Cancels every open prompt, for a sign-out or a teardown that invalidates them all. */
export function cancelAllSshPasswordPrompts(): void {
  // Snapshotted first: `settle` deletes from `pending`, and iterating it live would skip entries.
  for (const requestId of Array.from(pending.keys())) settle(requestId, null);
}
